import React, { useEffect } from "react";
import {
  AuthContextProvider,
  useAuth,
  IAuthContext,
} from "contexts/AuthContext";
import { Toaster } from "components/ui/sonner";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { ErrorBoundary } from "@sentry/react";
import { ErrorFallback } from "components/errors/ErrorFallbackScreen";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import { FileUploadProvider } from "./contexts/FileUploadContext";
import {
  FeatureFlagProvider,
  useFeatureFlags,
} from "./contexts/FeatureFlagContext";
import { LayoutProvider } from "./contexts/LayoutContext";
import { FeatureFlags } from "./integrations/launch-darkly/utils";
import { isMeticulousRecorderActive } from "./integrations/meticulous";
import { MeticulousSessionRecorder } from "./integrations/meticulous/MeticulousSessionRecorder";

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    auth: undefined! as IAuthContext,
    featureFlags: undefined! as FeatureFlags,
  },
  scrollRestoration: true,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  interface RouterContext {
    auth: IAuthContext;
    featureFlags: FeatureFlags;
  }
}

function useReloadRoutesWhenAuthChanges(isLoggedIn: boolean) {
  useEffect(() => {
    router.invalidate();
  }, [isLoggedIn]);
}

const InnerApp = () => {
  const auth = useAuth();
  const { features } = useFeatureFlags();
  useReloadRoutesWhenAuthChanges(auth.isLoggedIn);

  return (
    <RouterProvider
      router={router}
      context={{ auth, featureFlags: features }}
    />
  );
};

const App: React.FC = () => (
  <ErrorBoundary fallback={ErrorFallback}>
    <AuthContextProvider>
      <FeatureFlagProvider>
        {isMeticulousRecorderActive() && <MeticulousSessionRecorder />}
        <LayoutProvider>
          <FileUploadProvider>
            <InnerApp />
          </FileUploadProvider>
        </LayoutProvider>
      </FeatureFlagProvider>
      <Toaster />
    </AuthContextProvider>
  </ErrorBoundary>
);

export default App;
